
import {
  ellipsisString,
  formatPriceWithoutCurrency
} from "@/app/infrastructures/misc/Utils";
import { Options, Vue } from "vue-class-component";
import { MainAppController } from "../../controllers/MainAppController";

interface CheckColumn {
  column: string;
  type: "number";
}

@Options({
  emits: ["update:modelValue", "update:isClear", "change", "error"],
  props: {
    modelValue: {
      required: true
    },
    type: {
      default: "csv" || " xlsx", // csv, xlsx
      type: String
    },
    isClear: {
      default: false,
      type: Boolean
    },
    isDraggable: {
      default: true,
      type: Boolean
    },
    showInfoDraggable: {
      default: true,
      type: Boolean
    },
    showFileDetail: {
      default: true,
      type: Boolean
    },
    maxRows: {
      default: 100000,
      type: Number
    },
    maxSize: {
      default: 256, // size in MB
      type: Number
    },
    url: {
      default: "",
      type: String
    },
    name: {
      default: "",
      type: String
    },
    statusReset: {
      default: false,
      type: Boolean
    },
    isMax: {
      default: true,
      type: Boolean
    },
    customClass: {
      default: "",
      type: String
    },
    caption: {
      default: "",
      type: String
    },
    captionHtml: {
      default: "",
      type: String
    },
    allowed: {
      default: "",
      type: String
    },
    changeCustom: {
      default: false,
      type: Boolean
    },
    showPreviewImage: {
      default: false,
      type: Boolean
    },
    isShowRemarkError: {
      default: true,
      type: Boolean
    },
    titleChange: {
      default: "Ubah File",
      type: String
    },
    checkColumn: {
      default: [],
      type: Array as () => CheckColumn[]
    },
    archiveType: {
      default: "",
      type: String
    },
    disabled: {
      default: false,
      type: Boolean
    },
    errorMessage: {
      default: "",
      type: String
    },
    draggable: {
      default: "csv, xlsx, pdf",
      type: String
    },
    longName: {
      default: 50,
      type: Number
    },
    isClearable: {
      default: false,
      type: Boolean
    }
  },
  watch: {
    "props.isClear"() {
      if (this.props.isClear) this.setDefault();
    }
  }
})
export default class UploadFile extends Vue {
  props: any = this.$props;
  fileName = "";
  isDataValidate = false;
  isDataRemoved = false;
  loadingUpload = false;
  isDragFile = false;
  errorColumnMessage = "";

  $refs!: {
    file: HTMLFormElement;
  };

  get acceptTypes() {
    if (this.props.allowed) return this.props.allowed;
    return this.props.type
      .split(",")
      .map((ext: string) => `.${ext.trim()}`)
      .join(",");
  }

  onUploadCsv(files: any) {
    this.setDefault();
    // set isClear to false
    if (this.props.isClear) this.$emit("update:isClear", false);

    if (files && files.name.split(".").pop() === "csv") {
      const reader = new FileReader();
      reader.readAsBinaryString(files);
      reader.onload = () => {
        const arrData = reader.result
          ?.toString()
          .replace(/\r\n/g, "\n")
          .split("\n");
        if (arrData && !arrData[arrData.length - 1]) {
          arrData?.pop();
        }
        this.isDataValidate = !(
          arrData && arrData.length <= this.props.maxRows + 1
        );
        const columnToCheck = this.props.checkColumn.filter(
          (col: any) => col.type === "number"
        );
        if (!this.isDataValidate && arrData && !!columnToCheck.length) {
          const dataToCheck = arrData.map((data) => data.split(","));
          const errorColumns = this.onValidateColumn(dataToCheck);
          this.isDataValidate = !!errorColumns.length;
          if (!errorColumns.length) {
            this.setValue(files);
          } else {
            this.errorColumnMessage = `Terdapat value spasi di ${errorColumns.join(
              ", "
            )}.`;
          }
        } else if (!this.isDataValidate) {
          this.setValue(files);
        }
      };
    }
    this.isDataValidate = true;
  }

  onValidateColumn(arrData: string[][]) {
    const errorColumns: string[] = [];
    const columnToCheck = this.props.checkColumn.filter(
      (col: any) => col.type === "number"
    );

    const dataToCheck = [...arrData];
    dataToCheck.shift();
    for (const row of dataToCheck) {
      columnToCheck.forEach((col: any) => {
        const columnIndex = arrData[0].indexOf(col.column);
        const value = row[columnIndex];
        if (
          /\s/.test(value) &&
          !errorColumns.find((err) => err === col.column)
        ) {
          errorColumns.push(col.column);
        }
      });
      if (errorColumns.length === columnToCheck.length) {
        break;
      }
    }

    return errorColumns;
  }

  onReadFileExcel(reader: any, files: any) {
    reader.onload = async (e: any) => {
      const data = e.target?.result;
      this.loadingUpload = true;

      if (!data) {
        console.error("File data is not available");
        return;
      }

      const XLSX = await import("xlsx");
      const workbook = XLSX.read(data, { type: "binary" });
      const sheetNameList = workbook.SheetNames;

      const parseSheetData = (sheetName: string): any[] => {
        const worksheet = workbook.Sheets[sheetName];
        return XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          blankrows: false
        });
      };

      const validateSheetData = (parsedData: any[]): string[] => {
        return this.onValidateColumn(parsedData);
      };

      const processSheets = (
        isMaxRow: boolean
      ): { countRows: number; message: string } => {
        let totalRows = 0;
        let validationMessage = "";

        sheetNameList.forEach((sheetName) => {
          const parsedData = parseSheetData(sheetName);
          totalRows += parsedData.length;

          if (!isMaxRow) {
            const invalidColumns = validateSheetData(parsedData);
            if (invalidColumns.length) {
              const sheetMessage = `Sheet ${sheetName} kolom ${invalidColumns.join(
                ", "
              )}.`;
              validationMessage += validationMessage
                ? ` ${sheetMessage}`
                : `Terdapat value spasi di ${sheetMessage}`;
            }
          }
        });

        return { countRows: totalRows, message: validationMessage };
      };

      // Initial processing for max rows validation
      const { countRows, message: initialMessage } = processSheets(true);

      // Validate max rows
      this.isDataValidate = countRows > this.props.maxRows + 1;
      if (this.isDataValidate) {
        MainAppController.closeLoading();
        this.loadingUpload = false;
        return;
      }

      // Detailed processing if within max rows
      const { message: finalMessage } = processSheets(false);

      if (finalMessage) {
        this.errorColumnMessage = finalMessage;
        this.isDataValidate = true;
      } else {
        this.setValue(files);
      }

      MainAppController.closeLoading();
      this.loadingUpload = false;
    };
  }

  async onUploadExcel(files: any) {
    this.setDefault();
    this.loadingUpload = true;

    // set isClear to false
    if (this.props.isClear) this.$emit("update:isClear", false);

    const reader = new FileReader();

    if (files && files.name.split(".").pop() === "xlsx") {
      MainAppController.showLoading();
      await this.onReadFileExcel(reader, files);

      reader.readAsBinaryString(files);
    }
    this.loadingUpload = false;
  }

  onUploadPDFDocument(files: any) {
    if (
      files &&
      files.name
        .split(".")
        .pop()
        .match(/^pdf$|^doc$|^docx$/gi)
    ) {
      if (files.size <= 1024 * 1024 * this.props.maxSize) this.setValue(files);
      else this.isDataValidate = true;
    } else this.isDataValidate = true;

    this.$emit("error", this.isDataValidate);
  }

  formatMaxRows(param: any) {
    return formatPriceWithoutCurrency(param).replace(/,/g, ".");
  }

  setDragAndDrop(e: any, value: boolean) {
    this.isDragFile = value;
    e.preventDefault();
  }

  isFileImage = false;
  fileImagePreview: any = "";

  setValue(file: any) {
    this.fileName = file.name;
    this.$emit("update:modelValue", file);
    this.$emit("change", file);
    this.isDataValidate = false;
    this.isDragFile = false;
    this.isFileImage = false;
    if (file.type.includes("image")) {
      this.isFileImage = true;
      this.fileImagePreview = URL.createObjectURL(file);
    }
  }

  onSubmitClick(e: any) {
    try {
      const file = e.target.files;
      const type = file[0]?.name.split(".").pop();

      if (
        file.length > 0 &&
        (this.props.type.includes(type) || this.props.allowed.includes(type))
      ) {
        // Click
        const clickFile = e.target.files;
        this.isDataRemoved = false;

        if (
          !this.props.allowed &&
          type === "csv" &&
          this.props.type.includes("csv")
        )
          return this.onUploadCsv(clickFile[0]);
        else if (
          !this.props.allowed &&
          type === "xlsx" &&
          this.props.type.includes("xlsx")
        )
          return this.onUploadExcel(clickFile[0]);
        else if (type.match(/^pdf$|^doc$|^docx$/gi))
          return this.onUploadPDFDocument(clickFile[0]);
        else if (this.props.allowed.includes("image"))
          this.onUploadImage(clickFile[0]);
      } else {
        this.$emit("update:modelValue", "");
        if (file.length === 0) {
          this.isDataRemoved = true;
        } else {
          this.isDataValidate = true;
        }
      }
    } catch (err) {
      this.$emit("update:modelValue", "");
      this.isDataValidate = true;
    }
  }

  // Drag File
  onSubmitDrag(e: any) {
    e.preventDefault();
    try {
      if (e.dataTransfer.files.length > 0) {
        const dragFile = e.dataTransfer.files;
        const typeDrag =
          dragFile && dragFile[0].name && dragFile[0].name.split(".").pop();

        if (this.validateDraggable(dragFile, typeDrag, "csv"))
          return this.onUploadCsv(dragFile[0]);
        else if (this.validateDraggable(dragFile, typeDrag, "xlsx"))
          return this.onUploadExcel(dragFile[0]);
        else if (this.validateDraggable(dragFile, typeDrag, "pdf"))
          return this.onUploadPDFDocument(dragFile[0]);
        else this.setDefault();
      } else this.setDefault();
    } catch (err) {
      this.setDefault();
    }
  }

  validateDraggable(dragFile: any, typeDrag: any, type: string) {
    return this.props.draggable.includes(type) && dragFile && typeDrag === type;
  }

  setDefault() {
    this.$emit("update:modelValue", "");
    this.$emit("update-filename");
    this.$refs.file.value = "";
    this.fileName = "";
    this.errorColumnMessage = "";
    this.isDataValidate = false;
    this.loadingUpload = false;
    this.isDragFile = false;
  }

  // Ellipsis String
  ellipsisString(value: string) {
    return ellipsisString(value, this.props.longName);
  }

  onUploadImage(file: any) {
    const type = file.name.split(".").pop();
    if (type === "jpg" || type === "jpeg" || type === "png") {
      if (file.size <= 1024 * 1024 * this.props.maxSize) this.setValue(file);
      else this.isDataValidate = true;
    } else this.isDataValidate = true;

    this.$emit("error", this.isDataValidate);
  }

  maxRowsData(maxRows: any) {
    if (this.props.archiveType === "cargo-config") {
      return this.formatMaxRows(maxRows - 2);
    }
    if (this.props.archiveType === "pos-limit") {
      return this.formatMaxRows(maxRows - 1);
    }
    return this.formatMaxRows(maxRows);
  }
}
